import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/layout/PublicNav";
import { GoogleButton } from "@/pages/Login";
import { useAuth } from "@/context/AuthContext";

const PERKS = ["Free starter resources & templates", "Free community access", "Weekly founder newsletter", "AI tool directory"];

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: new URLSearchParams(window.location.search).get("email") || "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    const res = await register(form.name, form.email, form.password);
    setLoading(false);
    if (res.ok) { toast.success("Account created!"); navigate("/onboarding"); }
    else setError(res.error);
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="relative hidden bg-navy lg:block grain">
        <div className="relative flex h-full flex-col justify-center p-16">
          <Logo light />
          <h2 className="mt-10 font-heading text-4xl font-bold leading-tight text-white">Join thousands of Indian founders building smarter.</h2>
          <ul className="mt-8 space-y-4">
            {PERKS.map((p) => (
              <li key={p} className="flex items-center gap-3 text-slate-300"><CheckCircle2 className="h-5 w-5 text-emerald" /> {p}</li>
            ))}
          </ul>
        </div>
      </div>
      <div className="flex flex-col justify-center px-6 py-12 sm:px-12 lg:px-20">
        <div className="mx-auto w-full max-w-sm">
          <div className="lg:hidden"><Logo /></div>
          <h1 className="mt-8 font-heading text-3xl font-bold tracking-tight text-navy lg:mt-0">Create your free account</h1>
          <p className="mt-2 text-sm text-slate-500">Start with free resources. Upgrade anytime.</p>
          <div className="mt-8"><GoogleButton label="Sign up with Google" /></div>
          <div className="my-6 flex items-center gap-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
            <div className="h-px flex-1 bg-slate-200" /> or <div className="h-px flex-1 bg-slate-200" />
          </div>
          <form onSubmit={submit} className="space-y-4" data-testid="register-form">
            <div>
              <Label htmlFor="name">Full name</Label>
              <Input id="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="register-name" className="mt-1.5 h-11" placeholder="Your name" />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="register-email" className="mt-1.5 h-11" placeholder="you@business.in" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} data-testid="register-password" className="mt-1.5 h-11" placeholder="At least 6 characters" />
            </div>
            {error && <p className="text-sm font-medium text-red-600" data-testid="register-error">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="register-submit" className="h-11 w-full rounded-md bg-emerald font-semibold text-white hover:bg-emerald-700">
              {loading ? "Creating…" : "Create free account"}
            </Button>
          </form>
          <p className="mt-6 text-sm text-slate-500">Already a member? <Link to="/login" className="font-semibold text-emerald hover:underline" data-testid="register-to-login">Log in</Link></p>
        </div>
      </div>
    </div>
  );
}
